/**
 * Country-specific payment, tax, and copy defaults.
 * Keep in sync with backend CountryPaymentProfiles.java.
 */
import i18n from '../i18n';

const GATEWAY_KEYS = {
  RAZORPAY: 'GATEWAY_RAZORPAY',
  STRIPE: 'GATEWAY_STRIPE',
  PAYPAL: 'GATEWAY_PAYPAL',
  MOLLIE: 'GATEWAY_MOLLIE',
};

/** EEA/eurozone (+ CH) countries that use the EU payment profile. */
export const EU_PAYMENT_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES',
  'SE', 'CH',
]);

/** EU Production Ready admin page — same footprint as EU payment profile (not GB/US/IN/AE). */
export function isEuProductionReadinessCountry(countryCode) {
  const cc = String(countryCode || '').trim().toUpperCase();
  return EU_PAYMENT_COUNTRY_CODES.has(cc);
}

const PROFILES = {
  IN: {
    manualMethods: ['CASH', 'UPI', 'BANK_TRANSFER', 'NETBANKING', 'CREDIT_CARD', 'DEBIT_CARD', 'CHEQUE'],
    gateways: [GATEWAY_KEYS.RAZORPAY, GATEWAY_KEYS.STRIPE, GATEWAY_KEYS.PAYPAL],
    defaultPaymentMethod: 'UPI',
    usesUpi: true,
    usesIfsc: true,
  },
  US: {
    manualMethods: ['CASH', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'CHEQUE'],
    gateways: [GATEWAY_KEYS.STRIPE, GATEWAY_KEYS.PAYPAL],
    defaultPaymentMethod: 'CASH',
    usesUpi: false,
    usesIfsc: false,
  },
  CA: {
    manualMethods: ['CASH', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'CHEQUE'],
    gateways: [GATEWAY_KEYS.STRIPE, GATEWAY_KEYS.PAYPAL],
    defaultPaymentMethod: 'CASH',
    usesUpi: false,
    usesIfsc: false,
  },
  GB: {
    manualMethods: ['CASH', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'CHEQUE'],
    gateways: [GATEWAY_KEYS.STRIPE, GATEWAY_KEYS.PAYPAL],
    defaultPaymentMethod: 'CASH',
    usesUpi: false,
    usesIfsc: false,
  },
  AE: {
    manualMethods: ['CASH', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD'],
    gateways: [GATEWAY_KEYS.STRIPE, GATEWAY_KEYS.PAYPAL],
    defaultPaymentMethod: 'CASH',
    usesUpi: false,
    usesIfsc: false,
  },
  /** Virtual key for eurozone / EEA payment defaults (any EU_PAYMENT_COUNTRY_CODES). */
  EU: {
    manualMethods: ['CASH', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD'],
    gateways: [GATEWAY_KEYS.STRIPE, GATEWAY_KEYS.PAYPAL, GATEWAY_KEYS.MOLLIE],
    defaultPaymentMethod: 'CASH',
    usesUpi: false,
    usesIfsc: false,
  },
};

export function normalizeCountryCode(countryCode) {
  const cc = String(countryCode || '').trim().toUpperCase();
  if (PROFILES[cc] && cc !== 'EU') return cc;
  if (EU_PAYMENT_COUNTRY_CODES.has(cc)) return 'EU';
  return 'IN';
}

export function getCountryProfile(countryCode) {
  const cc = normalizeCountryCode(countryCode);
  return {
    ...PROFILES[cc],
    paymentReferencePlaceholder: paymentReferencePlaceholderForCountry(countryCode),
    bankRoutingLabel: bankRoutingLabelForCountry(countryCode),
  };
}

export function isMethodApplicableForCountry(countryCode, methodKey) {
  const key = String(methodKey || '').toUpperCase();
  const profile = PROFILES[normalizeCountryCode(countryCode)];
  if (key.startsWith('GATEWAY_')) {
    return profile.gateways.includes(key);
  }
  return profile.manualMethods.includes(key);
}

export function defaultPaymentMethodForCountry(countryCode) {
  return PROFILES[normalizeCountryCode(countryCode)].defaultPaymentMethod;
}

export function paymentReferencePlaceholderForCountry(countryCode) {
  const cc = normalizeCountryCode(countryCode);
  const key = `regional.paymentReference.${cc}`;
  if (i18n.exists(key)) return i18n.t(key);
  return i18n.t('regional.paymentReference.default');
}

export function bankRoutingLabelForCountry(countryCode) {
  const cc = normalizeCountryCode(countryCode);
  const key = `regional.bankRouting.${cc}`;
  if (i18n.exists(key)) return i18n.t(key);
  return i18n.t('regional.bankRouting.default');
}

export { GATEWAY_KEYS };
